"use client";

import { CheckIcon, CopyIcon } from "lucide-react";
import { useState } from "react";

/**
 * Read-only command/URL field with a copy button. Used for the install methods
 * that have no deep link (Claude Code command, bare server URL) so users
 * can grab the exact string in one click.
 */
export function MCPCopyField({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      // Clipboard API can reject without a secure context or user gesture; the
      // worst case is the user selects the text manually, so we swallow it.
      window.setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="border-marketing-border flex items-center gap-3 rounded-xl border bg-white px-4 py-3">
      <code className="text-marketing-text flex-1 overflow-x-auto font-mono text-sm whitespace-nowrap">
        {value}
      </code>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
        className="text-marketing-text-muted hover:text-marketing-text inline-flex shrink-0 items-center gap-1.5 text-sm font-medium transition-colors"
      >
        {copied ? (
          <CheckIcon className="h-4 w-4" />
        ) : (
          <CopyIcon className="h-4 w-4" />
        )}
        <span>{copied ? "Copied" : "Copy"}</span>
      </button>
    </div>
  );
}
